import React from 'react';
import { Button, Typography, Box, Stack, Link } from '../../ui/core';
import { useFeature } from '@growthbook/growthbook-react';
import { useAuth, useEnvironment, useThemeMode } from '../../hooks';
import HitchLogoFullIcon from '../../ui/icons/HitchLogoFull';
import { useHasPermission } from '../../helpers/api/auth/index';
import { PERMISSIONS } from '../../helpers/api/auth/permissions';

const DISPLAY_FONT =
  '"Haas-Grot-Disp-App", "Haas-Grot-Text-App", "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Helvetica, Arial, sans-serif';

// Token blocks spread onto the page root so all children inherit via CSS vars.
// The active set is chosen by the resolved theme mode (see useThemeMode), so the
// page honors the preference picked at login instead of the OS setting.
const darkTokens = {
  '--page-bg': '#000000',
  '--text-primary': '#FFFFFF',
  '--text-secondary': 'rgba(255, 255, 255, 0.55)',
  '--text-muted': 'rgba(255, 255, 255, 0.75)',
  '--card-bg': 'rgba(255, 255, 255, 0.04)',
  '--card-bg-hover': 'rgba(255, 255, 255, 0.07)',
  '--card-border': 'rgba(255, 255, 255, 0.1)',
  '--card-border-hover': 'rgba(255, 255, 255, 0.18)',
  '--card-shadow':
    '0 12px 32px rgba(0, 0, 0, 0.4), 0 1px 0 rgba(255, 255, 255, 0.05) inset',
  '--chip-bg': 'rgba(255, 255, 255, 0.06)',
  '--chip-bg-hover': 'rgba(255, 255, 255, 0.1)',
  '--chip-border': 'rgba(255, 255, 255, 0.1)',
  '--chip-border-hover': 'rgba(255, 255, 255, 0.2)',
  '--chip-text': 'rgba(255, 255, 255, 0.85)',
  '--avatar-shadow':
    '0 4px 12px rgba(0, 0, 0, 0.3), 0 1px 0 rgba(255, 255, 255, 0.12) inset',
  '--logo-opacity': 0.95,
};

const lightTokens = {
  '--page-bg': '#FFFFFF',
  '--text-primary': '#1d1d1f',
  '--text-secondary': 'rgba(0, 0, 0, 0.55)',
  '--text-muted': 'rgba(0, 0, 0, 0.65)',
  '--card-bg': 'rgba(255, 255, 255, 0.7)',
  '--card-bg-hover': '#FFFFFF',
  '--card-border': 'rgba(0, 0, 0, 0.08)',
  '--card-border-hover': 'rgba(0, 0, 0, 0.15)',
  '--card-shadow':
    '0 12px 32px rgba(0, 0, 0, 0.06), 0 1px 0 rgba(255, 255, 255, 0.8) inset',
  '--chip-bg': 'rgba(0, 0, 0, 0.04)',
  '--chip-bg-hover': 'rgba(0, 0, 0, 0.06)',
  '--chip-border': 'rgba(0, 0, 0, 0.08)',
  '--chip-border-hover': 'rgba(0, 0, 0, 0.16)',
  '--chip-text': '#1d1d1f',
  '--avatar-shadow':
    '0 2px 6px rgba(0, 0, 0, 0.1), 0 1px 0 rgba(255, 255, 255, 0.3) inset',
  '--logo-opacity': 1,
};

const PageAvatar = ({ color, backgroundColor, children }) => {
  return (
    <Box
      sx={{
        width: '44px',
        height: '44px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color,
        backgroundColor,
        borderRadius: '12px',
        fontSize: '20px',
        fontWeight: 700,
        letterSpacing: '-0.01em',
        flexShrink: 0,
        boxShadow: 'var(--avatar-shadow)',
      }}
    >
      {children}
    </Box>
  );
};

const HomeLink = ({ to, avatar, title, subtitle }) => {
  return (
    <Link
      to={to}
      sx={{
        textDecoration: 'none',
        padding: '24px',
        display: 'flex',
        flexDirection: 'column',
        width: '280px',
        borderRadius: '20px',
        backgroundColor: 'var(--card-bg)',
        backdropFilter: 'blur(30px) saturate(180%)',
        WebkitBackdropFilter: 'blur(30px) saturate(180%)',
        border: '1px solid var(--card-border)',
        boxShadow: 'var(--card-shadow)',
        transition: 'background-color 0.18s ease, border-color 0.18s ease, transform 0.18s ease',
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'var(--card-bg-hover)',
          borderColor: 'var(--card-border-hover)',
          transform: 'translateY(-2px)',
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing="14px" mb="14px">
        {avatar}
        <Typography
          sx={{
            fontSize: '19px',
            fontWeight: 500,
            letterSpacing: '-0.015em',
            color: 'var(--text-primary)',
          }}
        >
          {title}
        </Typography>
      </Stack>
      <Typography
        sx={{
          fontSize: '14px',
          lineHeight: 1.5,
          fontWeight: 400,
          color: 'var(--text-secondary)',
          letterSpacing: '-0.005em',
        }}
      >
        {subtitle}
      </Typography>
    </Link>
  );
};

const EnvironmentChip = ({ environment, environmentBackgroundColor }) => {
  if (!environment) return null;
  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        px: '12px',
        py: '6px',
        borderRadius: '999px',
        backgroundColor: 'var(--chip-bg)',
        border: '1px solid var(--chip-border)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
      }}
    >
      <Box
        sx={{
          width: '8px',
          height: '8px',
          borderRadius: '50%',
          backgroundColor: environmentBackgroundColor || 'currentColor',
          boxShadow: `0 0 8px ${environmentBackgroundColor || 'currentColor'}`,
        }}
      />
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 500,
          letterSpacing: '-0.005em',
          color: 'var(--text-muted)',
          textTransform: 'capitalize',
        }}
      >
        {environment}
      </Typography>
    </Box>
  );
};

const Home = () => {
  const auth = useAuth();
  const { environment, environmentBackgroundColor } = useEnvironment();
  const { resolvedMode } = useThemeMode();

  const hasChangeDriverStatusPermission = useHasPermission(PERMISSIONS.CHANGE_DRIVER_STATUS);
  const hasSendNotificationsPermission = useHasPermission(PERMISSIONS.SEND_DRIVER_NOTIFICATIONS);
  // The routes enforce these too — see PrivateRoute — because a hidden card is not
  // access control. Both are satisfied by ADMIN, so a superuser sees them either way.
  const canManageDrivers = useHasPermission(PERMISSIONS.MANAGE_DRIVERS);
  const canManageVehicles = useHasPermission(PERMISSIONS.MANAGE_VEHICLE_CATALOG);
  // No permission gate (any admin) — the card shows on the GrowthBook flag alone,
  // matching the nav rail item and the page's own gate.
  const showRiderDriverExclusions = useFeature('admin-rider-driver-exclusions').on;

  return (
    <Box
      sx={{
        ...(resolvedMode === 'light' ? lightTokens : darkTokens),
        position: 'relative',
        minHeight: '100vh',
        '@supports (min-height: 100dvh)': { minHeight: '100dvh' },
        width: '100%',
        backgroundColor: 'var(--page-bg)',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          px: { xs: 3, sm: 5 },
          pt: { xs: 3, sm: 4 },
          pb: { xs: 2, sm: 3 },
          boxSizing: 'border-box',
        }}
      >
        <HitchLogoFullIcon
          titleAccess="Hitch"
          sx={{
            display: 'block',
            width: 'auto',
            height: { xs: '19px', sm: '21px' },
            color: 'var(--text-primary)',
            opacity: 'var(--logo-opacity)',
          }}
        />
        <Stack direction="row" alignItems="center" spacing="12px">
          <EnvironmentChip
            environment={environment}
            environmentBackgroundColor={environmentBackgroundColor}
          />
          <Button
            onClick={auth.logout}
            sx={{
              height: '36px',
              px: '18px',
              borderRadius: '999px',
              fontSize: '13px',
              fontWeight: 500,
              letterSpacing: '-0.005em',
              textTransform: 'none',
              color: 'var(--chip-text)',
              backgroundColor: 'var(--chip-bg)',
              border: '1px solid var(--chip-border)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              boxShadow: 'none',
              transition: 'background-color 0.18s ease, border-color 0.18s ease',
              '&:hover': {
                backgroundColor: 'var(--chip-bg-hover)',
                borderColor: 'var(--chip-border-hover)',
                boxShadow: 'none',
              },
            }}
          >
            Logout
          </Button>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          px: { xs: 3, sm: 5 },
          pt: { xs: 6, sm: 10 },
          pb: { xs: 6, sm: 8 },
        }}
      >
        <Box textAlign="center" sx={{ mb: { xs: 5, sm: 8 } }}>
          <Typography
            component="h1"
            sx={{
              fontFamily: DISPLAY_FONT,
              fontSize: 'clamp(36px, 7vw, 56px)',
              lineHeight: 1.05,
              fontWeight: 500,
              letterSpacing: '-0.03em',
              color: 'var(--text-primary)',
              mb: { xs: 1, sm: 1.5 },
              whiteSpace: 'nowrap',
            }}
          >
            Command Center
          </Typography>
          <Typography
            sx={{
              fontSize: { xs: '15px', sm: '17px' },
              lineHeight: 1.45,
              fontWeight: 400,
              color: 'var(--text-secondary)',
              letterSpacing: '-0.005em',
            }}
          >
            Where would you like to go?
          </Typography>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: '20px',
            flexWrap: 'wrap',
            width: '100%',
            maxWidth: '1000px',
          }}
        >
          <HomeLink
            to="/trips/daily"
            avatar={
              <PageAvatar backgroundColor="#97D2BE" color="#0D3A17">
                T
              </PageAvatar>
            }
            title="Trips"
            subtitle="Track and edit past, current, and future trips"
          />
          <HomeLink
            to="/users?page=0&limit=13"
            avatar={
              <PageAvatar backgroundColor="#732C59" color="#FFFFFF">
                U
              </PageAvatar>
            }
            title="User Data"
            subtitle="Modify driver and rider data"
          />
          {hasChangeDriverStatusPermission && (
            <HomeLink
              to="/drivers-approval/drivers-triage/waitlist?page=0&limit=7"
              avatar={
                <PageAvatar backgroundColor="#FF6837" color="#86371E">
                  D
                </PageAvatar>
              }
              title="Driver Approvals"
              subtitle="Triage driver applications through our custom funnel"
            />
          )}
          {canManageDrivers && (
            <HomeLink
              to="/drivers"
              avatar={
                <PageAvatar backgroundColor="#C4B5FD" color="#4C1D95">
                  D
                </PageAvatar>
              }
              title="Drivers"
              subtitle="Every applicant and driver, their documents, and their standing"
            />
          )}
          <HomeLink
            to="/stations"
            avatar={
              <PageAvatar backgroundColor="#FCA5A5" color="#B91C1C">
                S
              </PageAvatar>
            }
            title="Stations"
            subtitle="Manage available stations"
          />
          <HomeLink
            to="/fleet-management"
            avatar={
              <PageAvatar backgroundColor="#BFDBFE" color="#1D4ED8">
                F
              </PageAvatar>
            }
            title="Fleet"
            subtitle="Manage fleet vehicles, drivers, and trip assignments"
          />
          {hasSendNotificationsPermission && (
            <HomeLink
              to="/notifications/drivers"
              avatar={
                <PageAvatar backgroundColor="#A5C8DB" color="#1B475E">
                  N
                </PageAvatar>
              }
              title="Notifications"
              subtitle="Send templated notifications to drivers by city"
            />
          )}
          {canManageVehicles && (
            <HomeLink
              to="/vehicles"
              avatar={
                <PageAvatar backgroundColor="#99F6E4" color="#0F766E">
                  V
                </PageAvatar>
              }
              title="Vehicles"
              subtitle="The approved make, model and year catalog drivers pick from"
            />
          )}
          <HomeLink
            to="/promotions"
            avatar={
              <PageAvatar backgroundColor="#FDE68A" color="#92400E">
                P
              </PageAvatar>
            }
            title="Promotions"
            subtitle="Create and manage promo codes"
          />
          {showRiderDriverExclusions && (
            <HomeLink
              to="/rider-driver-exclusions"
              avatar={
                <PageAvatar backgroundColor="#FECACA" color="#991B1B">
                  E
                </PageAvatar>
              }
              title="Rider-Driver Exclusions"
              subtitle="Rider-driver pairs ops has flagged not to ride together"
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Home;
